// The room's seeded PRNG (mechanics §0.3). All gameplay randomness (spawn points, colours, eject
// jitter, pop angles, bot choices) comes from one of these, so a room started with the same seed
// and fed the same inputs reproduces the same state.
//
// Algorithm: xoshiro128** (128-bit state, 32-bit output; public-domain design by Blackman and
// Vigna), seeded by running the seed through a golden-ratio counter and our `hash32` finaliser.
// Only 32-bit integer operations are used, so every JS engine produces the same sequence.

import { hash32 } from './hash.js';

const TWO_POW_32 = 4294967296;
const TAU = Math.PI * 2;

/** The full generator state: four unsigned 32-bit words, never all zero. */
export type RngState = readonly [number, number, number, number];

function rotl(x: number, k: number): number {
  return (x << k) | (x >>> (32 - k));
}

export class Rng {
  private s0 = 0;
  private s1 = 0;
  private s2 = 0;
  private s3 = 0;

  /**
   * @param seed any finite number. Integers in the safe range are fully used (the high 21 bits
   *   are folded into the low 32); fractional parts are ignored.
   */
  constructor(seed: number) {
    this.seed(seed);
  }

  /** Re-seeds the generator in place. */
  seed(seed: number): void {
    const s = Number.isFinite(seed) ? Math.trunc(seed) : 0;
    const hi = Math.floor(s / TWO_POW_32);
    let z = (s >>> 0) ^ hash32(hi >>> 0);
    const words: number[] = [];
    for (let i = 0; i < 4; i++) {
      z = (z + 0x9e3779b9) >>> 0;
      words.push(hash32(z ^ (z >>> 13)));
    }
    this.s0 = words[0] ?? 0;
    this.s1 = words[1] ?? 0;
    this.s2 = words[2] ?? 0;
    this.s3 = words[3] ?? 0;
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s0 = 1;
  }

  /** A uniform unsigned 32-bit integer. */
  nextU32(): number {
    const result = Math.imul(rotl(Math.imul(this.s1, 5), 7), 9) >>> 0;
    const t = this.s1 << 9;
    this.s2 ^= this.s0;
    this.s3 ^= this.s1;
    this.s1 ^= this.s2;
    this.s0 ^= this.s3;
    this.s2 ^= t;
    this.s3 = rotl(this.s3, 11);
    return result;
  }

  /** A uniform float in `[0, 1)` with 32 bits of resolution. */
  next(): number {
    return this.nextU32() / TWO_POW_32;
  }

  /** A uniform float in `[min, max)`. */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** A uniform integer in `[0, n)`. `n` must be a positive integer ≤ 2³². */
  int(n: number): number {
    return Math.floor(this.next() * n);
  }

  /** A uniform integer in `[min, max]` (both inclusive). */
  intRange(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** True with probability `p` (`p ≤ 0` is never, `p ≥ 1` is always). */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** A uniform angle in `[0, 2π)` radians. */
  angle(): number {
    return this.next() * TAU;
  }

  /** A uniformly chosen element. Throws on an empty array. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('Rng.pick: empty array');
    return items[this.int(items.length)] as T;
  }

  /** Shuffles `items` in place (Fisher–Yates) and returns it. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const a = items[i] as T;
      items[i] = items[j] as T;
      items[j] = a;
    }
    return items;
  }

  /** A new, independent generator derived from this one's next output and `salt`. */
  fork(salt = 0): Rng {
    return new Rng((this.nextU32() ^ hash32(salt >>> 0)) >>> 0);
  }

  /** A snapshot of the state; `setState` restores it exactly. */
  getState(): RngState {
    return [this.s0 >>> 0, this.s1 >>> 0, this.s2 >>> 0, this.s3 >>> 0];
  }

  setState(state: RngState): void {
    const [a, b, c, d] = state;
    if ((a | b | c | d) >>> 0 === 0) throw new RangeError('Rng.setState: all-zero state');
    this.s0 = a | 0;
    this.s1 = b | 0;
    this.s2 = c | 0;
    this.s3 = d | 0;
  }

  /** An exact copy that continues the same sequence independently. */
  clone(): Rng {
    const copy = new Rng(0);
    copy.setState(this.getState());
    return copy;
  }
}

/** Convenience factory: `createRng(seed)` is `new Rng(seed)`. */
export function createRng(seed: number): Rng {
  return new Rng(seed);
}
